<template>
  <v-autocomplete
    ref="autocomplete"
    v-model="selectedAddress"
    @change="onChange"
    @update:search-input="getLocationSuggestions"
    label="Search Address *"
    :items="branches"
    :filter="customFilter"
    item-text="label"
    item-value="value"
    hide-no-data
    hide-details
    outlined
    dense
    required
    clearable
    prepend-inner-icon="mdi-map-marker"
  >
    <template v-slot:item="{ item }">
      <v-list-item-content class="w-[600px]">
        <v-list-item-title>{{ item.label }}</v-list-item-title>
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script>
/* global google */
import { debounce } from '@/utils/common'

export default {
  name: 'CommonAutocomplete',
  props: {
    addressLine1: String,
    branch: Array
  },
  data() {
    return {
      selectedAddress: null,
      branches: [],
      placesServiceData: null,
      googleMapsLoaded: false
    }
  },
  watch: {
    addressLine1: {
      handler(newVal) {
        if (newVal) this.selectedAddress = newVal
      },
      immediate: true
    },
    branch: {
      handler(newVal) {
        this.branches = newVal
      },
      immediate: true
    }
  },
  methods: {
    onChange(val) {
      if (!this.googleMapsLoaded) {
        this.loadGoogleMapsScript()
          .then(() => {
            // Retry after loading
            this.processOnChange(val)
          })
          .catch((error) => {
            console.error('Failed to load Google Maps:', error)
            // Emit with available data
            this.$emit('selected-place', {
              formatted_address: val || '',
              city: '',
              state: '',
              country: '',
              pincode: '',
              countryCode: '',
              location: null
            })
          })
        return
      }

      // If we get here, Google Maps should be loaded
      this.processOnChange(val)
    },

    processOnChange(val) {
      if (!this.googleMapsLoaded) {
        console.error('Google Maps not loaded yet.')
        // Emit with available data
        this.$emit('selected-place', {
          formatted_address: val || '',
          city: '',
          state: '',
          country: '',
          pincode: '',
          countryCode: '',
          location: null
        })
        return
      }

      // If val is empty, clear the selection
      if (!val) {
        this.selectedAddress = null
        return
      }

      const selectedBranch = this.branches.find((branch) => branch.value === val)
      if (selectedBranch) {
        this.selectedAddress = selectedBranch.label
      }

      // If val is not a place_id from our branches, it might be a direct input
      const placeId = selectedBranch ? selectedBranch.value : val

      // If it looks like a place ID (starts with 'Ch' and is long enough), get details
      if (placeId && placeId.startsWith('Ch') && placeId.length > 20) {
        this.getPlaceDetails(placeId)
      } else {
        // Emit with the selected value as formatted address if we can't get details
        this.$emit('selected-place', {
          formatted_address: this.selectedAddress || val || '',
          city: '',
          state: '',
          country: '',
          pincode: '',
          countryCode: '',
          location: null
        })
      }
    },

    getPlaceDetails(placeId) {
      if (!this.googleMapsLoaded) {
        console.error('Google Maps not properly initialized.')
        return
      }

      const request = {
        placeId: placeId,
        fields: ['address_component', 'formatted_address', 'name', 'geometry']
      }

      const service = new google.maps.places.PlacesService(document.createElement('div'))
      service.getDetails(request, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const addressComponents = place.address_components || []
          const branchData = {}

          for (const component of addressComponents) {
            for (const type of component.types) {
              if (type === 'locality') {
                branchData.city = component.long_name
              } else if (type === 'administrative_area_level_1') {
                branchData.state = component.long_name
              } else if (type === 'route') {
                branchData.address = component.long_name
              } else if (type === 'postal_code') {
                branchData.pinCode = component.long_name
              } else if (type === 'country') {
                branchData.country = component.long_name
                branchData.countryCode = component.short_name
              } else if (type === 'street_number') {
                branchData.streetNumber = component.long_name
              } else if (type === 'sublocality') {
                branchData.sublocality = component.long_name
              }
            }
          }
          this.selectedAddress = place.formatted_address
          this.$emit('selected-place', {
            formatted_address: place.formatted_address,
            city: branchData.city || '',
            state: branchData.state || '',
            country: branchData.country || '',
            pincode: branchData.pinCode || '',
            countryCode: branchData.countryCode || '',
            location: place.geometry?.location || null
          })
        } else {
          console.error('Failed to get place details:', status)
          // Emit with the selected value as formatted address if we can't get details
          this.$emit('selected-place', {
            formatted_address: this.selectedAddress || '',
            city: '',
            state: '',
            country: '',
            pincode: '',
            countryCode: '',
            location: null
          })
        }
      })
    },
    getLocationSuggestions: debounce(function (searchTxt) {
      if (!searchTxt || searchTxt.length < 3) {
        return
      }

      // console.log('Searching for:', searchTxt)

      // If Google Maps is not loaded yet, try to load it
      if (!this.googleMapsLoaded) {
        this.loadGoogleMapsScript()
          .then(() => {
            // Retry the search after loading
            this.performSearch(searchTxt)
          })
          .catch((error) => {
            console.error('Failed to load Google Maps:', error)
          })
        return
      }

      // If we get here, Google Maps should be loaded
      this.performSearch(searchTxt)
    }, 500),

    performSearch(searchTxt) {
      if (!this.googleMapsLoaded) {
        console.error('Google Maps not loaded yet.')
        return
      }

      const service = new google.maps.places.PlacesService(document.createElement('div'))
      service.textSearch(
        {
          query: searchTxt
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && results) {
            this.branches = results.map((result) => ({
              label: result.formatted_address,
              value: result.place_id
            }))
          } else {
            console.error('Places search failed:', status)
            this.branches = []
          }
        }
      )
    },
    customFilter() {
      return true
    },
    async loadGoogleMapsScript() {
      // Check if already loaded
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        this.initPlacesService()
        this.googleMapsLoaded = true
        return Promise.resolve()
      }

      // Check if API key is available
      const apiKey = process.env.VUE_APP_MAP_KEY
      if (!apiKey) {
        console.error('Google Maps API key not found in environment variables')
        return Promise.reject(new Error('Google Maps API key not configured'))
      }

      return new Promise((resolve, reject) => {
        // Check if script is already being loaded
        if (window.googleMapsLoading) {
          // Wait for existing load to complete
          window.googleMapsCallbacks = window.googleMapsCallbacks || []
          window.googleMapsCallbacks.push(() => {
            this.initPlacesService()
            this.googleMapsLoaded = true
            resolve()
          })
          return
        }

        // Mark as loading
        window.googleMapsLoading = true
        window.googleMapsCallbacks = []

        // Create script element
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
        script.async = true
        script.defer = true

        script.onload = () => {
          this.initPlacesService()
          this.googleMapsLoaded = true

          // Execute callbacks
          window.googleMapsCallbacks.forEach((callback) => callback())
          window.googleMapsCallbacks = []
          window.googleMapsLoading = false

          resolve()
        }

        script.onerror = () => {
          console.error('Failed to load Google Maps script')
          window.googleMapsLoading = false
          reject(new Error('Failed to load Google Maps script'))
        }

        document.head.appendChild(script)
      })
    },
    initPlacesService() {
      this.placesServiceData = new google.maps.places.PlacesService(document.createElement('div'))
    }
  },
  mounted() {
    // Handle initial addressLine1 prop correctly
    if (this.$props.addressLine1) {
      if (typeof this.$props.addressLine1 === 'string') {
        this.selectedAddress = this.$props.addressLine1
      } else if (this.$props.addressLine1.label) {
        this.selectedAddress = this.$props.addressLine1.label
      } else {
        this.selectedAddress = this.$props.addressLine1
      }
    }
    this.branches = this.$props.branch || []
    this.loadGoogleMapsScript()
  }
}
</script>

<style scoped></style>
